import { EChartsOption } from 'echarts';
import { Observable } from 'rxjs';

const WHITE_VALUES = new Set(['#fff', '#ffffff']);

/**
 * Standard Tailwind gray hex values commonly hardcoded in content-page
 * ECharts configs, mapped to their CSS custom-property equivalents.
 *
 * Only applied in dark mode — the original Tailwind grays look correct
 * on a light background, but are invisible on a dark one.
 */
const TAILWIND_GRAY_TO_CSS_VAR: Array<[string, string]> = [
  ['#f3f4f6', '--neutral-100'], // gray-100 → grid / split lines
  ['#e5e7eb', '--neutral-200'], // gray-200
  ['#d1d5db', '--neutral-300'], // gray-300 → axis lines
  ['#9ca3af', '--neutral-400'], // gray-400
  ['#6b7280', '--neutral-500'], // gray-500 → axis labels
];

/** Deep-walk, strip white backgrounds, and optionally remap colours. */
function walkOptions(obj: unknown, colorMap: Map<string, string> | null): unknown {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => walkOptions(item, colorMap));
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    // Strip white backgroundColor (invisible in light, broken in dark)
    if (key === 'backgroundColor' && typeof value === 'string' && WHITE_VALUES.has(value.toLowerCase())) {
      continue;
    }
    if (typeof value === 'string' && colorMap) {
      result[key] = colorMap.get(value.toLowerCase()) ?? value;
    } else if (typeof value === 'object') {
      result[key] = walkOptions(value, colorMap);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Observable that emits `true` when the `<body>` has class `dark`, `false`
 * otherwise. Works regardless of which mechanism toggles dark mode
 * (ColorSchemeService, content-app toggle, etc.) by watching the body's
 * class attribute via MutationObserver.
 */
export function watchBodyDarkMode$(doc: Document): Observable<boolean> {
  return new Observable<boolean>((subscriber) => {
    const check = () => doc.body.classList.contains('dark');
    subscriber.next(check());
    const observer = new MutationObserver(() => subscriber.next(check()));
    observer.observe(doc.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  });
}

/**
 * Sanitise an ECharts option tree for the current theme.
 *
 * Always:
 *  - Strips white `backgroundColor` values (invisible on white, broken on dark).
 *
 * Dark mode only:
 *  - Replaces hardcoded Tailwind gray hex values with the project's
 *    CSS-custom-property equivalents so axes / labels / grid lines are visible.
 */
export function remapEchartsColors(options: EChartsOption, doc: Document, isDark: boolean): EChartsOption {
  let colorMap: Map<string, string> | null = null;

  if (isDark) {
    const style = getComputedStyle(doc.body);
    colorMap = new Map<string, string>();
    for (const [hex, cssVar] of TAILWIND_GRAY_TO_CSS_VAR) {
      const resolved = style.getPropertyValue(cssVar).trim();
      if (resolved) {
        colorMap.set(hex, resolved);
      }
    }
    if (colorMap.size === 0) {
      colorMap = null;
    }
  }

  return walkOptions(options, colorMap) as EChartsOption;
}
